import { useEffect, type ReactNode } from "react";

/**
 * The one layout every "you can't use this here" screen shares: the denied
 * feature (in the shell) and paused team access (outside it).
 *
 * A hairline card, a muted glyph tile, a heading that names the thing, one or
 * two plain sentences, then the actions — one primary at most. Centered in the
 * content column; on a phone it fills the width with the 16px gutter.
 */
export function AccessCard({
  icon,
  eyebrow,
  title,
  children,
  actions,
  titleAs: TitleTag = "h1",
}: {
  icon: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** "h2" when the page frame above already renders the page's `<h1>`. */
  titleAs?: "h1" | "h2";
}) {
  return (
    <section className="mx-auto w-full max-w-[560px] rounded-[16px] border border-[color:var(--border-light)] bg-[color:var(--surface)] p-5 sm:p-7">
      <span
        aria-hidden="true"
        className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-[12px] bg-[color:var(--surface-alt)] text-[color:var(--text-secondary)] [&_svg]:h-[22px] [&_svg]:w-[22px]"
      >
        {icon}
      </span>
      {eyebrow ? (
        <p className="m-0 mb-1 text-[13px] font-semibold text-[color:var(--text-muted)]">
          {eyebrow}
        </p>
      ) : null}
      <TitleTag className="m-0 text-[22px] font-bold leading-tight tracking-[-0.02em] text-[color:var(--text-primary)]">
        {title}
      </TitleTag>
      {children ? (
        <div className="mt-2 flex flex-col gap-2 text-[15px] leading-[1.6] text-[color:var(--text-secondary)]">
          {children}
        </div>
      ) : null}
      {actions ? (
        <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
          {actions}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Names the page in the browser tab while it is shown, and gives the previous
 * title back when it goes. Re-applied after a tick because the shell sets the
 * store name in an effect that runs after its children's on a first load.
 */
export function useDocumentTitle(title: string) {
  useEffect(() => {
    let previous: string | null = null;
    const apply = () => {
      if (document.title === title) return;
      previous = document.title;
      document.title = title;
    };
    apply();
    const timer = window.setTimeout(apply, 0);
    return () => {
      window.clearTimeout(timer);
      if (previous !== null && document.title === title) document.title = previous;
    };
  }, [title]);
}

export function SparkleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16.5l-1.8-5L5 9.7l5.2-1.8z" />
      <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
    </svg>
  );
}

export function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="10.5" width="16" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function PauseIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9v6M14 9v6" />
    </svg>
  );
}
